import { useMemo, useState } from "react";
import { Database, HardDrive, KeyRound, Monitor, Search, Server } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Badge } from "../../../ui/common-ui/badge";

export interface GroupedResource {
  name: string;
  resourceType?: string;
  activityCount?: number;
}

const RESOURCE_ICONS: Record<string, LucideIcon> = {
  "virtual-machine": Monitor,
  VM: Monitor,
  EC2: Monitor,
  "storage-bucket": HardDrive,
  "Storage Bucket": HardDrive,
  S3: HardDrive,
  database: Database,
  Database: Database,
  RDS: Database,
  secret: KeyRound,
  Secret: KeyRound,
};

export function ResourceGroupList({ resources }: { resources: GroupedResource[] }) {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const sorted = [...resources].sort((a, b) => Number(b.activityCount || 0) - Number(a.activityCount || 0));
    if (!query.trim()) return sorted;
    const needle = query.trim().toLowerCase();
    return sorted.filter((resource) => resource.name?.toLowerCase().includes(needle));
  }, [resources, query]);

  const active = resources.filter((resource) => Number(resource.activityCount || 0) > 0).length;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 rounded-lg border border-border/40 bg-muted/20 px-2 py-1.5">
        <Search className="w-3.5 h-3.5 text-muted-foreground" />
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search resources..."
          className="flex-1 bg-transparent text-xs outline-none text-foreground placeholder:text-muted-foreground"
        />
      </div>
      <p className="text-[10px] text-muted-foreground">
        {active} of {resources.length} have recorded activity
      </p>
      <div className="max-h-72 space-y-1.5 overflow-y-auto pr-1">
        {filtered.map((resource) => {
          const Icon = RESOURCE_ICONS[String(resource.resourceType)] || Server;
          const activity = Number(resource.activityCount || 0);
          return (
            <div
              key={resource.name}
              className="flex items-center gap-2 rounded-lg border border-border/40 bg-muted/20 p-2"
            >
              <Icon className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
              <span className="min-w-0 flex-1 truncate text-xs font-mono text-foreground">{resource.name}</span>
              <Badge variant={activity > 0 ? "secondary" : "outline"} className="text-[10px]">
                {activity > 0 ? `${activity} actions` : "idle"}
              </Badge>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <p className="py-4 text-center text-xs text-muted-foreground">No matching resources</p>
        )}
      </div>
    </div>
  );
}
